import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, ArrowRight, CalendarClock, Check, Circle, CircleCheck, Keyboard, Layers, PartyPopper,
  RotateCcw, RotateCw, Shuffle, Target,
} from 'lucide-react';
import { flashcardProgressService } from '../services/api';
import { Badge, Button, ProgressBar } from './ui';
import './FlashcardViewer.css';

// Escala de qualidade da repetição espaçada (SM-2)
const RATINGS = [
  { quality: 0, label: 'Esqueci', tone: 'danger', title: 'Esqueci completamente' },
  { quality: 1, label: 'Errei', tone: 'warning', title: 'Errei mas lembrei algo' },
  { quality: 3, label: 'Difícil', tone: 'neutral', title: 'Acertei com dificuldade' },
  { quality: 4, label: 'Bom', tone: 'navy', title: 'Acertei bem' },
  { quality: 5, label: 'Fácil', tone: 'lime', title: 'Perfeito, muito fácil' },
];

function FlashcardViewer({ flashcards = [], topicName = '', topicId = null, courseSlug = null }) {
  // Cada cartão guarda a posição original no JSON do tópico (originalIndex): é ela que identifica
  // o progresso no servidor, mesmo depois de embaralhar.
  const withIndex = (list) => list.map((c, i) => ({ ...c, originalIndex: i }));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cards, setCards] = useState(() => withIndex(flashcards));
  const [mastered, setMastered] = useState(new Set());
  const [cardProgress, setCardProgress] = useState({});

  useEffect(() => {
    setCards(withIndex(flashcards));
    setCurrentIndex(0);
    setIsFlipped(false);
    setMastered(new Set());
    if (topicId && courseSlug) {
      loadProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flashcards, topicId, courseSlug]);

  const loadProgress = async () => {
    try {
      const progress = await flashcardProgressService.getProgressByTopic(topicId, courseSlug);
      const progressMap = {};
      progress.forEach(p => { progressMap[p.cardIndex] = p; });
      setCardProgress(progressMap);
      setMastered(new Set(progress.filter(p => p.mastered).map(p => p.cardIndex)));
    } catch (error) {
      console.error('Error loading flashcard progress:', error);
    }
  };

  const original = cards[currentIndex]?.originalIndex;

  const handleRate = async (quality) => {
    if (!topicId || !courseSlug) return;
    try {
      const result = await flashcardProgressService.reviewCard(topicId, original, courseSlug, quality);
      setCardProgress(prev => ({ ...prev, [original]: result }));
    } catch (error) {
      console.error('Error reviewing card:', error);
    }
    setIsFlipped(false);
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleFlip = useCallback(() => {
    setIsFlipped(f => !f);
  }, []);

  const handleNext = useCallback(() => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  }, [currentIndex, cards.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  }, [currentIndex]);

  const handleShuffle = () => {
    // Fisher-Yates: embaralhamento uniforme (sort com random é enviesado)
    const shuffled = [...cards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleMastered = async () => {
    const value = !mastered.has(original);
    setMastered(prev => {
      const next = new Set(prev);
      if (value) next.add(original);
      else next.delete(original);
      return next;
    });
    if (topicId && courseSlug) {
      try {
        await flashcardProgressService.setMastered(topicId, original, courseSlug, value);
      } catch (error) {
        console.error('Error saving mastered flag:', error);
      }
    }
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          handleFlip();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          handlePrev();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFlip, handleNext, handlePrev]);

  if (!cards || cards.length === 0) {
    return (
      <div className="fc-empty">
        <Layers size={28} aria-hidden="true" />
        <div>
          <strong>Nenhum flashcard disponível ainda</strong>
          <span>Adicione flashcards em JSON para revisar este tópico.</span>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];
  const progress = ((currentIndex + 1) / cards.length) * 100;
  const isMastered = mastered.has(currentCard.originalIndex);

  return (
    <div className="fc">
      <div className="fc-top">
        <span className="fc-top__count">
          <strong>{String(currentIndex + 1).padStart(2, '0')}</strong> / {String(cards.length).padStart(2, '0')}
        </span>
        <ProgressBar value={progress} label="Posição no baralho" className="fc-top__bar" />
        <Badge tone="lime" icon={Check}>{mastered.size} dominados</Badge>
      </div>

      <div
        className="fc-stage"
        onClick={handleFlip}
        role="button"
        tabIndex={0}
        aria-label={isFlipped ? 'Mostrar pergunta' : 'Mostrar resposta'}
      >
        <div className={`fc-card ${isFlipped ? 'is-flipped' : ''} ${isMastered ? 'is-mastered' : ''}`}>
          <div className="fc-face fc-face--front">
            <span className="fc-face__label">Pergunta</span>
            <div className="fc-face__content">{currentCard.front}</div>
            <span className="fc-face__hint"><RotateCw size={14} aria-hidden="true" /> Clique ou aperte espaço para virar</span>
          </div>
          <div className="fc-face fc-face--back">
            <span className="fc-face__label">Resposta</span>
            <div className="fc-face__content">{currentCard.back}</div>
            <span className="fc-face__hint"><RotateCw size={14} aria-hidden="true" /> Clique para voltar</span>
          </div>
        </div>
      </div>

      {topicId && courseSlug && isFlipped && (
        <div className="fc-rate">
          <span className="fc-rate__label">Como foi?</span>
          <div className="fc-rate__buttons">
            {RATINGS.map(r => (
              <button
                key={r.quality}
                type="button"
                className={`fc-rate__btn fc-rate__btn--${r.tone}`}
                onClick={() => handleRate(r.quality)}
                title={r.title}
              >
                {r.label}
              </button>
            ))}
          </div>
          {cardProgress[currentCard.originalIndex] && (
            <div className="fc-rate__info">
              <span><CalendarClock size={13} aria-hidden="true" /> Próxima revisão: {cardProgress[currentCard.originalIndex].nextReviewDate || 'hoje'}</span>
              <span><Target size={13} aria-hidden="true" /> Acertos: {cardProgress[currentCard.originalIndex].correctReviews}/{cardProgress[currentCard.originalIndex].totalReviews}</span>
            </div>
          )}
        </div>
      )}

      <div className="fc-controls">
        <Button variant="outline" icon={ArrowLeft} onClick={handlePrev} disabled={currentIndex === 0}>
          Anterior
        </Button>
        <div className="fc-controls__mid">
          <Button
            variant={isMastered ? 'accent' : 'ghost'}
            icon={isMastered ? CircleCheck : Circle}
            onClick={handleMastered}
            title="Marcar como dominado"
          >
            {isMastered ? 'Dominado' : 'Marcar'}
          </Button>
          <Button variant="ghost" icon={Shuffle} onClick={handleShuffle}>Embaralhar</Button>
        </div>
        <Button iconRight={ArrowRight} onClick={handleNext} disabled={currentIndex === cards.length - 1}>
          Próximo
        </Button>
      </div>

      <p className="fc-keys">
        <Keyboard size={14} aria-hidden="true" /> <kbd>Espaço</kbd> vira · <kbd>←</kbd> <kbd>→</kbd> navega
      </p>

      {mastered.size === cards.length && (
        <div className="fc-done">
          <PartyPopper size={24} aria-hidden="true" />
          <span>Parabéns! Você dominou todos os {cards.length} flashcards.</span>
          <Button size="sm" variant="accent" icon={RotateCcw} onClick={handleReset}>Revisar novamente</Button>
        </div>
      )}
    </div>
  );
}

export default FlashcardViewer;
